/**
 * Document head adapter — modern implementation (React 19 / Vite brands).
 *
 * React 19 hoists `<title>`, `<meta>` and `<link>` into `<head>` on its own, so children are
 * rendered in place. `htmlAttributes` are written to the response during SSR (applied to the
 * `<html>` tag by `entry.server`) and to `document.documentElement` on the client.
 */
import React, {useContext, useEffect} from 'react';

import {ServerContext} from '../sdk/modern';

export type HeadProps = {
	children?: React.ReactNode;
	htmlAttributes?: {lang?: string; dir?: string};
};

export const Head = ({children, htmlAttributes}: HeadProps) => {
	const ctx = useContext(ServerContext) as {res?: {locals?: Record<string, unknown>}} | null;

	if (typeof window === 'undefined' && htmlAttributes && ctx?.res?.locals) {
		ctx.res.locals.htmlAttributes = {
			...(ctx.res.locals.htmlAttributes as Record<string, string>),
			...htmlAttributes,
		};
	}

	useEffect(() => {
		Object.entries(htmlAttributes || {}).forEach(([name, value]) => {
			if (value) {
				document.documentElement.setAttribute(name, value);
			}
		});
	}, [htmlAttributes?.lang, htmlAttributes?.dir]);

	// `<script>` text (JSON-LD) is rendered by React as is, except `</script`, which it escapes: content coming
	// from the CMS or the catalog cannot close the script element. No `dangerouslySetInnerHTML` here.
	return <>{children}</>;
};

export default Head;
